import { FC, useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import Assignments from 'src/components/assignments';
import { LoadingScreen } from 'src/components/common/loaders';
import ElementarySchoolView from 'src/components/elementary-school-view';
import StudentNav from 'src/components/global-nav/student-nav';
import MiddleSchoolView from 'src/components/middle-school-view';
import { TopBanner } from 'src/components/top-banner';
import { MIDDLE_SCHOOL_GRADES } from 'src/constants/grades.constants';
import {
  useK5PastWorkTeacherAssignments,
  useK5ToDoTeacherAssignments,
  useMSTeacherAssignments,
} from 'src/hooks/teacher-assignments';
import useCurrentGrade from 'src/hooks/use-current-grade.hook';
import { useDesmosLesson } from 'src/hooks/use-desmos-lesson.hook';
import { useInitialSelectedClass } from 'src/hooks/use-initial-selected-class';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { useLoadingScreen } from 'src/hooks/use-loading-screen.hook';
import { useMsProductCheck } from 'src/hooks/use-ms-product-check.hook';
import { useNavigationApiDataPassing } from 'src/hooks/use-navigation-api-data-passing';
import { usePresentation } from 'src/hooks/use-presentation.hook';
import { useTaughtLessons } from 'src/hooks/use-taught-lessons';
import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import { useUserCustomGroups } from 'src/hooks/use-user-custom-groups';
import i18n from 'src/i18n';
import { AssignmentsViewOptions } from 'src/interfaces/assignment.types';
import { Screens } from 'src/interfaces/screens.types';
import { CurrentAppViewOptions } from 'src/interfaces/store.types';
import AuthService from 'src/services/api/auth.service';
import {
  setActiveAssignmentsView,
  setCurrentAppView,
  setIsViewDefined,
  useCurrentGradeSelector,
  useIsGradeDefinedSelector,
  useIsViewDefinedSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import './view-wrapper.scss';

const ViewWrapper: FC = () => {
  // set selectedClass for teachers to be able to define current grade and view
  useInitialSelectedClass();

  const { isGradesLoading } = useCurrentGrade();

  const setLoadingScreenVisibility = useLoadingScreen();

  const userCurrentGrade = useCurrentGradeSelector();
  const isGradeDefined = useIsGradeDefinedSelector();

  const isMiddleSchoolView = useIsMiddleSchoolView();
  const isViewDefined = useIsViewDefinedSelector();

  const dispatch = useAppDispatch();

  const isTeacher = AuthService.isTeacher();

  const [currentScreen, setCurrentScreen] = useState(Screens.loading);

  const { isLoading: isMsProductCheckLoading } = useMsProductCheck();
  const {
    isLoadingWithoutError: isUserAssetsLoading,
    isError: isUserAssetsError,
  } = useUserAssets();

  // do not call it outside of ErrorBoundary as it includes queries call
  useNavigationApiDataPassing();

  const {
    isLoading: isK5ToDoTeacherAssignmentsLoading,
    error: k5ToDoTeacherAssignmentsError,
  } = useK5ToDoTeacherAssignments();
  const {
    isLoading: isMSTeacherAssignmentsLoading,
    error: mSTeacherAssignmentsError,
  } = useMSTeacherAssignments();
  const {
    isLoading: isK5PastWorkTeacherAssignmentsLoading,
    error: k5PastWorkTeacherAssignmentsError,
  } = useK5PastWorkTeacherAssignments();

  const isTeacherAssignmentsLoading =
    (isK5ToDoTeacherAssignmentsLoading && !k5ToDoTeacherAssignmentsError) ||
    (isMSTeacherAssignmentsLoading && !mSTeacherAssignmentsError) ||
    (isK5PastWorkTeacherAssignmentsLoading &&
      !k5PastWorkTeacherAssignmentsError);
  const { isLoading: isTaughtLessonsLoading } = useTaughtLessons();

  const isLoadingPresentation = usePresentation();
  const isDesmosLessonLoading = useDesmosLesson();

  const { isLoadingWithoutError: isUserCustomGroupsLoading } =
    useUserCustomGroups();

  // taught lessons are fetched during initial app load only in MS View
  const isMsViewTaughtLessonsLoading =
    isMiddleSchoolView && isTaughtLessonsLoading;

  const shouldShowLoadingScreen =
    isLoadingPresentation ||
    isDesmosLessonLoading ||
    (isUserAssetsLoading && !isUserAssetsError) ||
    isTeacherAssignmentsLoading ||
    !isViewDefined ||
    isGradesLoading ||
    !isGradeDefined ||
    isMsViewTaughtLessonsLoading ||
    isMsProductCheckLoading ||
    isUserCustomGroupsLoading;

  useEffect(() => {
    if (shouldShowLoadingScreen) {
      setLoadingScreenVisibility(false);
      setCurrentScreen(Screens.loading);
      return;
    }

    setCurrentScreen(Screens.homeScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowLoadingScreen]);

  useEffect(() => {
    if (!isGradeDefined) return;

    const hasMiddleSchoolGrade =
      MIDDLE_SCHOOL_GRADES.includes(userCurrentGrade);

    if (hasMiddleSchoolGrade) {
      dispatch(setCurrentAppView(CurrentAppViewOptions.MIDDLE_SCHOOL));
      dispatch(setActiveAssignmentsView(AssignmentsViewOptions.TODO));
    } else {
      dispatch(setCurrentAppView(CurrentAppViewOptions.ELEMENTARY_SCHOOL));
    }
    dispatch(setIsViewDefined(true));
  }, [userCurrentGrade, dispatch, isGradeDefined]);

  const screenMap = {
    loading: <LoadingScreen isJoiningLesson={isLoadingPresentation} />,
    homeScreen: <Assignments />,
  };

  return (
    <I18nextProvider i18n={i18n}>
      <StudentNav adultsView={isMiddleSchoolView} />
      {isTeacher && (
        <div className="view-wrapper__top-banner-container">
          <TopBanner />
        </div>
      )}
      {isMiddleSchoolView ? (
        <MiddleSchoolView>{screenMap[currentScreen]}</MiddleSchoolView>
      ) : (
        <ElementarySchoolView currentScreen={currentScreen}>
          {screenMap[currentScreen]}
        </ElementarySchoolView>
      )}
    </I18nextProvider>
  );
};

export default ViewWrapper;
